import React, { memo, useCallback } from "react";
import PropTypes from "prop-types";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import _ from "lodash";
import { DragHandle, ListItem } from "./components";

const MODIFIERS = [restrictToVerticalAxis, restrictToParentElement];

/**
 * DragNDropSortableList - A reusable drag and drop sortable list component
 *
 * This component provides drag and drop functionality for vertically sortable lists.
 * It uses @dnd-kit library for handling drag operations and supports keyboard navigation.
 * Motive behind this component is to encapsulate common drag-and-drop logic, still providing flexibility
 * to customize the list items and drag handles via child components.
 *
 * @component
 * @example
 * const [items, setItems] = useState([{ id: '1' }, { id: '2' }]);
 * const [activeItem, setActiveItem] = useState(null);
 *
 * <DragNDropSortableList
 *   items={items}
 *   onReorder={setItems}
 *   setActive={setActiveItem}
 * >
 *   {items.map(item => (
 *     <DragNDropSortableList.ListItem key={item.id} id={item.id}>
 *       <DragNDropSortableList.DragHandle id={item.id} />
 *       {item.content}
 *     </DragNDropSortableList.ListItem>
 *   ))}
 * </DragNDropSortableList>
 */
const DragNDropSortableList = ({ items, onReorder, children, setActive }) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor)
  );

  const handleDragStart = useCallback(
    e => {
      const { active } = e;
      if (setActive) setActive(active.data.current);
    },
    [setActive]
  );

  const handleDragEnd = useCallback(
    e => {
      const { active, over } = e;
      if (setActive) setActive(null);

      if (over && active.id !== over.id) {
        const oldIndex = _.findIndex(items, { id: active.id });
        const newIndex = _.findIndex(items, { id: over.id });
        const updatedOrder = arrayMove(items, oldIndex, newIndex);

        onReorder(updatedOrder);
      }
    },
    [items, onReorder, setActive]
  );

  const handleDragCancel = useCallback(() => {
    if (setActive) setActive(null);
  }, [setActive]);

  return (
    <DndContext
      sensors={sensors}
      modifiers={MODIFIERS}
      onDragCancel={handleDragCancel}
      onDragEnd={handleDragEnd}
      onDragStart={handleDragStart}
    >
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        {children}
      </SortableContext>
    </DndContext>
  );
};

DragNDropSortableList.propTypes = {
  /** Array of items to be sorted. Each item must have an 'id' property */
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    })
  ).isRequired,
  /** Callback function invoked when items are reordered. Receives updated items array */
  onReorder: PropTypes.func.isRequired,
  /** Child components to render within the sortable context */
  children: PropTypes.node.isRequired,
  /** Callback function to set the currently active (dragging) item */
  setActive: PropTypes.func,
};

const MemoizedDragNDropSortableList = memo(DragNDropSortableList);
MemoizedDragNDropSortableList.ListItem = ListItem;
MemoizedDragNDropSortableList.DragHandle = DragHandle;

export default MemoizedDragNDropSortableList;
